import { Modal } from "@mui/material";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { useModalConfirm } from "@ryde/redux";
import {
  ConfirmResult,
  Log,
  rideCancelConfirmAtom,
  RideDeleteActionsResultEnum,
  useSystemParams,
} from "@ryde/shared";

import {
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeaderStyled,
  MuiButton,
} from "../controls";
import {
  CancelationReasonFieldStyled,
  ConfirmCancelRidePromptContent,
} from "./confirms.styled";

const MODAL_WIDTH = "530px";

export const ConfirmRideCancelContainer: FC = () => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const [confirmData, setRideCancelData] = useRecoilState(
    rideCancelConfirmAtom,
  );
  const [cancelationReason, setCancelationReason] = useState("");

  const { markBookingAsNewOnRideDeletedOrCanceled } = useSystemParams();

  const isModalOpen = useMemo(() => {
    return Boolean(confirmData);
  }, [confirmData]);

  const requireCancelationReason = Boolean(
    confirmData?.requireCancelationReason,
  );

  const trimmedCancelationReason = cancelationReason.trim();

  // RYDE-7633: a past taxi ride is cancelled locally without notifying the provider, so the reason is
  // the only audit trail — the confirming actions stay disabled until one is entered. The backend
  // rejects such a cancel without a reason too; this only keeps the user from hitting that error.
  const isCancelationReasonMissing =
    requireCancelationReason && !trimmedCancelationReason;

  const closeModal = useCallback(() => {
    setRideCancelData(null);
    setCancelationReason("");
  }, [setRideCancelData]);

  const okClickHandler = useCallback(
    (result: RideDeleteActionsResultEnum) => async () => {
      if (!confirmData) {
        Log.warnAndToast(t("errors.INVALID_DELETE_HANDLER_DATA"));
        return;
      }

      const { resolveFn } = confirmData;
      const reason = requireCancelationReason
        ? trimmedCancelationReason || undefined
        : undefined;

      // Guard the non-UI entry points (Escape/X resolve NoAction and are always allowed).
      if (
        result !== RideDeleteActionsResultEnum.NoAction &&
        isCancelationReasonMissing
      ) {
        return;
      }

      if (result === RideDeleteActionsResultEnum.CancelBooking) {
        const confirmationResult = await confirmDialogResult({
          title: t("ride.CAUTION_TITLE"),
          message: t("ride.THIS_ACTION_WILL_CANCEL_BOOKINGS"),
          okButtonTitle: t("ride.REMOVE_BOOKINGS"),
          cancelButtonTitle: t("ride.CANCEL_THIS_ACTION"),
        });

        if (confirmationResult === ConfirmResult.OK) {
          resolveFn({ bookingAction: result, cancelationReason: reason });
        } else {
          resolveFn({ bookingAction: RideDeleteActionsResultEnum.NoAction });
        }
      } else {
        resolveFn({ bookingAction: result, cancelationReason: reason });
      }

      Log.debug("Ride cancel handler with result:", result, confirmData);
      closeModal();
    },
    [
      closeModal,
      confirmData,
      confirmDialogResult,
      isCancelationReasonMissing,
      requireCancelationReason,
      trimmedCancelationReason,
      t,
    ],
  );

  const ModalHeader = useMemo(() => {
    return (
      <ModalHeaderStyled>
        <h1>
          {t(
            markBookingAsNewOnRideDeletedOrCanceled
              ? `general.ARE_YOU_SURE`
              : `general.ATTENTION`,
          )}
        </h1>
        <SVG_MUI.XMarkSVG
          onClick={okClickHandler(RideDeleteActionsResultEnum.NoAction)}
        />
      </ModalHeaderStyled>
    );
  }, [markBookingAsNewOnRideDeletedOrCanceled, okClickHandler, t]);

  const ModalContent = useMemo(() => {
    return (
      <ModalContentStyled>
        <ConfirmCancelRidePromptContent>
          <span className="ride_numbers">
            {confirmData?.rideIds?.length || 1}
          </span>
          <span className="main_message">
            {t(`ride.PROMPTS.BULK_CANCEL_RIDES_MAIN_MESSAGE`)}
          </span>
          {!markBookingAsNewOnRideDeletedOrCanceled && (
            <span className="sub_message">
              {t(`ride.PROMPTS.BULK_CANCEL_RIDES_SUB_MESSAGE`)}
            </span>
          )}
        </ConfirmCancelRidePromptContent>
        {requireCancelationReason && (
          <CancelationReasonFieldStyled>
            <label htmlFor="ride-cancelation-reason">
              {t(`ride.PROMPTS.CANCELATION_REASON_LABEL`)}
            </label>
            <textarea
              id="ride-cancelation-reason"
              value={cancelationReason}
              required={true}
              aria-required={true}
              placeholder={t(`ride.PROMPTS.CANCELATION_REASON_PLACEHOLDER`)}
              onChange={event => setCancelationReason(event.target.value)}
            />
          </CancelationReasonFieldStyled>
        )}
      </ModalContentStyled>
    );
  }, [
    markBookingAsNewOnRideDeletedOrCanceled,
    confirmData?.rideIds?.length,
    requireCancelationReason,
    cancelationReason,
    t,
  ]);

  const ModalFooter = useMemo(() => {
    return (
      <ModalFooterStyled>
        <MuiButton
          muiStyle={"primary"}
          muiVariant={"outlined"}
          onClick={okClickHandler(RideDeleteActionsResultEnum.NoAction)}
          e2eData="cancel-modal-button"
        >
          {t(
            markBookingAsNewOnRideDeletedOrCanceled
              ? `general.CANCEL`
              : `general.CLOSE`,
          )}
        </MuiButton>
        {!markBookingAsNewOnRideDeletedOrCanceled && (
          <MuiButton
            muiVariant={"outlined"}
            muiStyle={"warning"}
            disabled={isCancelationReasonMissing}
            onClick={okClickHandler(RideDeleteActionsResultEnum.CancelBooking)}
            e2eData="remove-completely-booking-button"
          >
            {t(`ride.PROMPTS.RIDES_REMOVE_BOOKING_BUTTON`)}
          </MuiButton>
        )}
        <MuiButton
          disabled={isCancelationReasonMissing}
          onClick={okClickHandler(RideDeleteActionsResultEnum.NewBooking)}
          e2eData="ok-booking-button"
        >
          {t(
            markBookingAsNewOnRideDeletedOrCanceled
              ? `general.OK`
              : `ride.PROMPTS.RIDES_KEEP_BOOKING_BUTTON`,
          )}
        </MuiButton>
      </ModalFooterStyled>
    );
  }, [
    isCancelationReasonMissing,
    markBookingAsNewOnRideDeletedOrCanceled,
    okClickHandler,
    t,
  ]);

  return (
    <>
      {isModalOpen && (
        <Modal open={isModalOpen} closeAfterTransition={true}>
          <ModalContainerStyled width={MODAL_WIDTH}>
            {ModalHeader}
            {ModalContent}
            {ModalFooter}
          </ModalContainerStyled>
        </Modal>
      )}
    </>
  );
};
